import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog.jsx';
import { Button } from '@/components/ui/button.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { api } from '@/lib/api.js';
import { formatDate } from '@/lib/date-display.js';

// The identity half of the Fleet-editing UI (AIRCRAFT-CANONICALIZATION-PHASE1
// spec; built prompt 163 §4). Edits the facts that OUTLIVE a term — make,
// model, year, seats, serial, gear, default base airport — through the single
// writer (upsertAircraftIdentity). This screen existing is the point: a
// hand-rename in Zoho was the only way to change a tail, and that is how one
// airplane's history ended up wearing another's name (Dejoli).
//
// THE FORM LOADS FROM THE STORE, NOT FROM THE CALLER (2026-09-24, N42TG). The
// caller hands over a tail; the dialog reads GET /aircraft/:tail/identity and
// keeps that row as the BASELINE. Save sends only the keys that differ from
// it. Before this it prefilled off a pipeline row carrying year and a joined
// make/model, rendered five blanks over five seeded facts, and Save sent every
// blank as '' — which the door writes as NULL. Until the row has loaded, or if
// the read fails, there is nothing to diff against and Save stays disabled:
// a save that cannot say what it is changing must not run.
//
// THE TAIL IS NOT A PLAIN FIELD. Editing it asks the question only the person
// typing can answer (same rule as the roster door): correcting the tail keeps
// the airplane and renames its records with provenance; a different aircraft
// is a change of subject and is refused here, pointed at the roster where the
// quotes get dispositioned. Server warnings surface as toasts — a warning
// routed to the console is a warning that was not issued.
//
// CONFIRM (David 2026-10-30): with nothing changed Save stays disabled, so a
// registry value that is right could never be stated. Each field says where
// its value came from (field_tiers, derived on the server); an unconfirmed,
// unedited value carries "Confirm <value>", which records it as the broker's
// with who and when. The door refuses a value that moved since this read.

const FIELDS = ['make', 'model', 'year', 'seats', 'serial_number', 'undercarriage_type', 'default_base_airport'];
const LABELS = { make: 'make', model: 'model', year: 'year', seats: 'seats', serial_number: 'serial number', undercarriage_type: 'gear', default_base_airport: 'base airport' };
const formFrom = (row) => Object.fromEntries(FIELDS.map((k) => [k, row?.[k] == null ? '' : String(row[k])]));
const SOURCE_WORDS = {
  faa: 'from the FAA registry, unconfirmed',
  amin: 'from the AMIN lookup, unconfirmed',
  term: 'copied from a policy term, unconfirmed',
};
function sourceLine(t) {
  if (!t) return '';
  if (t.tier === 'verified') return `confirmed by ${t.actor || 'a broker'}${t.at ? `, ${formatDate(t.at)}` : ''}`;
  if (t.tier === 'entered') return `typed by ${t.actor || 'someone'}${t.at ? `, ${formatDate(t.at)}` : ''}, not checked against a source`;
  return SOURCE_WORDS[t.tier] || 'source not recorded, unconfirmed';
}

export default function AircraftIdentityDialog({ aircraft, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState(formFrom(null));
  const [baseline, setBaseline] = useState(null);   // the row as the store holds it; null until loaded
  const [loadState, setLoadState] = useState('idle'); // idle | loading | ready | error
  const [registry, setRegistry] = useState(null);     // read-only class/engine/source lines
  const [tiers, setTiers] = useState(null);           // field → { tier, human, actor, at }, from the server
  const [confirming, setConfirming] = useState(null); // the field whose Confirm is in flight
  const [gearOptions, setGearOptions] = useState([]);
  const [tail, setTail] = useState('');
  const [intent, setIntent] = useState(null); // 'correction' | 'different_aircraft'
  const [registryAck, setRegistryAck] = useState(false);
  const [needsAck, setNeedsAck] = useState(false);
  const [saving, setSaving] = useState(false);

  const origTail = String(aircraft?.tail_number || '').trim().toUpperCase();
  const tailChanged = tail.trim().toUpperCase() !== origTail && tail.trim() !== '';

  useEffect(() => {
    if (!open || !aircraft) return;
    let cancelled = false;
    setTail(origTail);
    setIntent(null);
    setRegistryAck(false);
    setNeedsAck(false);
    setForm(formFrom(null));
    setBaseline(null);
    setRegistry(null);
    setTiers(null);
    setLoadState('loading');
    api.get(`/api/tracon/aircraft/${encodeURIComponent(origTail)}/identity`)
      .then((res) => {
        if (cancelled) return;
        const row = res?.aircraft || null;
        setBaseline(formFrom(row));
        setForm(formFrom(row));
        setGearOptions(Array.isArray(res?.undercarriage_options) ? res.undercarriage_options : []);
        setRegistry(row ? { aircraft_type: row.aircraft_type || '', engine_type: row.engine_type || '', identity_source: row.identity_source || '' } : null);
        setTiers(res?.field_tiers || null);
        setLoadState('ready');
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadState('error');
        toast.error(`Could not load ${origTail}'s identity: ${err?.body?.error || err.message || 'request failed'}`);
      });
    return () => { cancelled = true; };
  }, [open, aircraft, origTail]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const changedKeys = baseline ? FIELDS.filter((k) => String(form[k] ?? '') !== String(baseline[k] ?? '')) : [];

  async function handleConfirm(k) {
    if (confirming || loadState !== 'ready' || !baseline || baseline[k] === '') return;
    setConfirming(k);
    try {
      const res = await api.patch(`/api/tracon/aircraft/${encodeURIComponent(origTail)}/identity`, { confirm: { [k]: baseline[k] } });
      if (res?.success === false) throw new Error(res?.error || 'Confirm failed');
      // The answer is the stored row and its tiers: render from that, not from what was sent.
      const row = res?.aircraft || null;
      if (row) {
        const stored = formFrom(row);
        setBaseline((b) => ({ ...b, [k]: stored[k] }));
        setForm((f) => ({ ...f, [k]: stored[k] }));
      }
      if (res?.field_tiers) setTiers(res.field_tiers);
      const t = res?.field_tiers?.[k];
      if (t?.tier === 'verified') toast.success(`${origTail} ${LABELS[k]} ${row?.[k] ?? baseline[k]} confirmed as stated by ${t.actor || 'you'}`);
      else toast.error(`${origTail} ${LABELS[k]}: the confirm did not stick (now ${sourceLine(t) || 'unknown'})`);
      onSaved?.();
    } catch (err) {
      toast.error(`${origTail} ${LABELS[k]} not confirmed: ${err?.body?.error || err.message || 'request failed'}`);
    } finally {
      setConfirming(null);
    }
  }

  async function handleSave() {
    if (saving || loadState !== 'ready') return;
    if (tailChanged && !intent) { toast.error('Changing the tail needs an answer: correcting it, or a different aircraft?'); return; }
    if (tailChanged && intent === 'different_aircraft') return; // button disabled; belt and braces
    if (!tailChanged && changedKeys.length === 0) { toast.info(`${origTail}: nothing changed`); return; }
    setSaving(true);
    try {
      // Only what changed rides the PATCH. An absent key is "leave alone" at
      // the door; a present '' is "clear it", and only a field the broker
      // actually emptied gets there.
      const body = Object.fromEntries(changedKeys.map((k) => [k, form[k]]));
      if (tailChanged) {
        body.tail_number = tail.trim().toUpperCase();
        body.tail_change_intent = 'correction';
        if (registryAck) body.tail_change_ack = true;
      }
      const res = await api.patch(`/api/tracon/aircraft/${encodeURIComponent(origTail)}/identity`, body);
      if (res?.success === false) throw new Error(res?.error || 'Save failed');
      // Prompt 176 §4: a warning separate from (and quieter than) the success
      // reads as noise; a skipped mirror then looks like a failed save. The
      // outcome sentence carries the warning so "saved" and "but" arrive
      // together and neither can be missed alone.
      const warns = res?.warnings || [];
      const outcome = tailChanged
        ? `${origTail} corrected to ${tail.trim().toUpperCase()} (${res.rows_corrected ?? 0} term row${(res.rows_corrected ?? 0) === 1 ? '' : 's'} follow)`
        : `${origTail} identity SAVED (${changedKeys.join(', ')})`;
      if (warns.length) toast.info(`${outcome}. ${warns.join(' ')}`);
      else toast.success(outcome);
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      if (err?.body?.code === 'tail_correction_needs_confirmation') {
        setNeedsAck(true);
        toast.info(`${tail.trim().toUpperCase()} is not in the FAA registry. Confirm below if the correction is right anyway.`);
      } else {
        toast.error(err?.body?.error || err.message || 'Save failed');
      }
    } finally {
      setSaving(false);
    }
  }

  const inputCls = 'mt-1 w-full rounded-[5px] border border-border bg-bg px-2 py-1.5 text-sm text-text disabled:opacity-60';
  const disabled = loadState !== 'ready';
  // Where the value came from, and the Confirm door for an unconfirmed one
  // the broker has not edited (an edit is itself the statement, on Save).
  const provenance = (k) => {
    const t = tiers?.[k];
    if (!t || !baseline || baseline[k] === '') return null;
    const dirty = String(form[k] ?? '') !== String(baseline[k] ?? '');
    return (
      <span className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11px] text-text-dim">
        <span>{sourceLine(t)}</span>
        {!t.human && !dirty ? (
          <button
            type="button"
            className="text-accent underline-offset-2 hover:underline disabled:opacity-60"
            disabled={disabled || saving || !!confirming}
            onClick={() => handleConfirm(k)}
            title={`Record ${LABELS[k]} ${baseline[k]} on ${origTail} as stated by you`}
          >
            {confirming === k ? 'Confirming…' : `Confirm ${baseline[k]}`}
          </button>
        ) : null}
      </span>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Aircraft identity: {origTail}</DialogTitle>
          <DialogDescription>
            The facts that outlive a term. Coverage for this term lives behind the Coverage button.
          </DialogDescription>
        </DialogHeader>
        {loadState === 'loading' ? <p className="text-xs text-text-muted">Loading what TRACON knows about {origTail}…</p> : null}
        {loadState === 'error' ? <p className="text-xs text-accent">The identity could not be loaded, so nothing here can be saved. Close and try again.</p> : null}
        {loadState === 'ready' && !registry ? <p className="text-xs text-text-muted">No identity on file yet for {origTail}. Saving seeds one.</p> : null}
        <div className="grid grid-cols-2 gap-3">
          <label className="col-span-2 text-xs text-text-muted">
            Tail number
            <input
              value={tail}
              disabled={disabled}
              onChange={(e) => { setTail(e.target.value.toUpperCase()); setNeedsAck(false); setRegistryAck(false); }}
              className={`${inputCls} font-mono`}
            />
          </label>
          {tailChanged ? (
            <div className="col-span-2 rounded-card border border-warning/40 bg-warning/5 p-2.5 text-xs">
              <p className="font-medium text-text">You changed the tail. Which is it?</p>
              <label className="mt-1.5 flex items-start gap-2">
                <input type="radio" name="tail-intent" checked={intent === 'correction'} onChange={() => setIntent('correction')} />
                <span>Same aircraft, correcting the tail. Every record follows with the old spelling kept as provenance.</span>
              </label>
              <label className="mt-1 flex items-start gap-2">
                <input type="radio" name="tail-intent" checked={intent === 'different_aircraft'} onChange={() => setIntent('different_aircraft')} />
                <span>A different aircraft. That is a change of subject: make it on the deal roster, where its quotes get dispositioned.</span>
              </label>
              {needsAck && intent === 'correction' ? (
                <label className="mt-2 flex items-start gap-2 text-warning">
                  <input type="checkbox" checked={registryAck} onChange={(e) => setRegistryAck(e.target.checked)} />
                  <span>{tail.trim().toUpperCase()} is not in the FAA registry. I am sure this correction is right.</span>
                </label>
              ) : null}
            </div>
          ) : null}
          <label className="text-xs text-text-muted">Make
            <input value={form.make} disabled={disabled} onChange={set('make')} className={inputCls} />
            {provenance('make')}
          </label>
          <label className="text-xs text-text-muted">Model
            <input value={form.model} disabled={disabled} onChange={set('model')} className={inputCls} />
            {provenance('model')}
          </label>
          <label className="text-xs text-text-muted">Year
            <input value={form.year} disabled={disabled} onChange={set('year')} inputMode="numeric" className={inputCls} />
            {provenance('year')}
          </label>
          <label className="text-xs text-text-muted">Seats
            <input value={form.seats} disabled={disabled} onChange={set('seats')} inputMode="numeric" className={inputCls} />
            {provenance('seats')}
          </label>
          <label className="text-xs text-text-muted">Serial number
            <input value={form.serial_number} disabled={disabled} onChange={set('serial_number')} className={`${inputCls} font-mono`} />
            {provenance('serial_number')}
          </label>
          <label className="text-xs text-text-muted">Gear
            <select value={form.undercarriage_type} disabled={disabled} onChange={set('undercarriage_type')} className={inputCls}>
              <option value="">Not stated</option>
              {gearOptions.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
            {provenance('undercarriage_type')}
          </label>
          <label className="col-span-2 text-xs text-text-muted">Default base airport
            <input value={form.default_base_airport} disabled={disabled} onChange={set('default_base_airport')} className={`${inputCls} font-mono`} />
          </label>
          {registry && (registry.aircraft_type || registry.engine_type) ? (
            <p className="col-span-2 text-[11px] text-text-dim">
              Registry: {[registry.aircraft_type, registry.engine_type].filter(Boolean).join(' · ')}
            </p>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button
            onClick={handleSave}
            disabled={saving || disabled || (tailChanged && (!intent || intent === 'different_aircraft')) || (needsAck && !registryAck) || (!tailChanged && changedKeys.length === 0)}
          >
            {saving ? 'Saving…' : tailChanged ? 'Correct the tail' : changedKeys.length ? `Save ${changedKeys.length} change${changedKeys.length === 1 ? '' : 's'}` : 'Save identity'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
